import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  ScanImage,
  ScanDebugResponse,
  ScanDebugView,
  ScanDebugImage,
  GenerationLogResponse,
  ScanIntermediatesResponse,
  ScanMetadataResponse,
} from '../types';
import { fetchScanDebug, fetchGenerationLog, fetchScanIntermediates, fetchScanMetadata } from '../hooks/useJobs';
import { Lightbox } from './ImagesModal';
import { createLogger } from '../utils/logger';

const log = createLogger('ScanDebugModal');

interface ScanDebugModalProps {
  isOpen: boolean;
  jobId: string | null;
  scanId: string | null;
  onClose: () => void;
}

type TabId = 'views' | 'metadata' | 'intermediates' | 'log';

function inferExtension(url: string): string {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\.([a-zA-Z0-9]{1,5})$/);
    if (match) return match[1].toLowerCase();
  } catch {
    // ignore parse errors
  }
  return 'jpg';
}

async function downloadImage(image: ScanImage, scanId: string | null): Promise<void> {
  const ext = inferExtension(image.url);
  const filename = `${scanId ?? 'image'}_${image.label}.${ext}`;
  try {
    const response = await fetch(image.url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    log.error(`Failed to download image ${filename}:`, err);
    window.open(image.url, '_blank', 'noopener,noreferrer');
  }
}

interface ThumbProps {
  image: ScanDebugImage;
  onOpen: () => void;
}

function Thumb({ image, onOpen }: ThumbProps) {
  const [errored, setErrored] = useState(false);
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block bg-surface-50 rounded-lg overflow-hidden border border-surface-200 hover:border-primary-400 hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-primary-400 text-left"
      title="Click to enlarge"
    >
      <div className="aspect-video bg-surface-100 flex items-center justify-center overflow-hidden">
        {errored ? (
          <span className="text-surface-400 text-xs p-4">Failed to load</span>
        ) : (
          <img
            src={image.url}
            alt={image.label}
            loading="lazy"
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
            onError={() => setErrored(true)}
          />
        )}
      </div>
      <div className="px-2 py-1.5">
        <span className="text-xs font-medium text-surface-700 truncate block">
          {image.label.replace(/_/g, ' ')}
        </span>
      </div>
    </button>
  );
}

interface ComparePaneProps {
  title: string;
  images: ScanDebugImage[];
  index: number;
  onIndex: (next: number) => void;
  onZoom: (image: ScanDebugImage) => void;
}

function ComparePane({ title, images, index, onIndex, onZoom }: ComparePaneProps) {
  const total = images.length;
  const image = images[index];
  const go = (delta: number) => {
    if (total === 0) return;
    onIndex((index + delta + total) % total);
  };
  return (
    <div className="flex-1 min-w-0 min-h-0 flex flex-col bg-black/30 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-3 py-2 bg-black/40 text-white/90">
        <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
        {image && (
          <span className="text-xs font-mono text-white/70 truncate">
            {index + 1}/{total} &middot; {image.label.replace(/_/g, ' ')}
          </span>
        )}
      </div>
      <div className="relative flex-1 min-h-0 flex items-center justify-center p-2">
        {image ? (
          <img
            src={image.url}
            alt={image.label}
            className="max-w-full max-h-full object-contain cursor-zoom-in"
            onClick={() => onZoom(image)}
          />
        ) : (
          <span className="text-white/50 text-sm italic">No image</span>
        )}
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={() => go(-1)}
              aria-label="Previous"
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={() => go(1)}
              aria-label="Next"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

interface ViewsTabProps {
  data: ScanDebugResponse;
  scanId: string | null;
}

function ViewsTab({ data, scanId }: ViewsTabProps) {
  const [selectedId, setSelectedId] = useState<string | null>(
    data.views[0]?.id ?? null
  );
  const [preview, setPreview] = useState<{ images: ScanImage[]; index: number } | null>(null);
  const [copied, setCopied] = useState(false);
  const [promptExpanded, setPromptExpanded] = useState(false);
  const [compare, setCompare] = useState<{ outIdx: number; inIdx: number } | null>(null);

  const selected: ScanDebugView | null = useMemo(
    () => data.views.find((v) => v.id === selectedId) ?? data.views[0] ?? null,
    [data.views, selectedId]
  );

  // Reset the prompt/compare overlays whenever the active view changes.
  useEffect(() => {
    setPromptExpanded(false);
    setCompare(null);
  }, [selectedId]);

  // Combined list used for lightbox navigation on the selected view.
  const previewImages = useMemo<ScanImage[]>(() => {
    if (!selected) return [];
    return [...selected.outputs, ...selected.inputs];
  }, [selected]);

  const openPreview = (image: ScanDebugImage) => {
    const idx = previewImages.findIndex((it) => it.url === image.url);
    if (idx >= 0) setPreview({ images: previewImages, index: idx });
  };

  const copyPrompt = async () => {
    if (!selected?.prompt) return;
    try {
      await navigator.clipboard.writeText(selected.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      log.error('Failed to copy prompt:', err);
    }
  };

  if (data.views.length === 0) {
    return (
      <div className="p-12 text-center text-surface-400 italic">
        No per-view debug data available for this scan.
      </div>
    );
  }

  const aftermarket = data.aftermarket;
  const coverValidation = data.cover_validation;
  const coverValidationFailed = !!coverValidation && !coverValidation.valid;

  return (
    <div className="relative flex flex-col h-full min-h-0">
      {coverValidationFailed && (
        <div className="shrink-0 border-b border-red-200 bg-red-50 px-4 py-2.5">
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 mt-0.5 shrink-0 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-red-800 uppercase tracking-wider">
                Cover (OV) validation failed
                {coverValidation.failed_covers.length > 0 && (
                  <span className="ml-2 font-normal normal-case text-red-700">
                    {coverValidation.failed_covers.join(', ')}
                  </span>
                )}
              </p>
              <p className="text-xs text-red-700 mt-0.5 break-words">
                {Object.entries(coverValidation.covers)
                  .map(([pos, status]) => `${pos}: ${status}`)
                  .join('  ·  ') ||
                  'One or more cover images failed headlight/taillight validation.'}
              </p>
            </div>
          </div>
        </div>
      )}
      {aftermarket?.has_aftermarket && (
        <div className="shrink-0 border-b border-amber-200 bg-amber-50 px-4 py-2.5">
          <div className="flex items-start gap-2">
            <svg
              className="w-4 h-4 mt-0.5 shrink-0 text-amber-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
              />
            </svg>
            <div className="min-w-0">
              <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                Aftermarket wheels/tires detected
                <span className="ml-2 font-normal normal-case text-amber-700">
                  {[
                    aftermarket.has_aftermarket_rims ? 'rims' : null,
                    aftermarket.has_aftermarket_tires ? 'tires' : null,
                  ]
                    .filter(Boolean)
                    .join(' + ') || 'flagged'}
                </span>
              </p>
              {aftermarket.description && (
                <p className="text-xs text-amber-700 mt-0.5 break-words">
                  {aftermarket.description}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="relative flex flex-col md:flex-row flex-1 min-h-0">
      {/* View list */}
      <div className="md:w-56 md:shrink-0 md:border-r md:border-b-0 border-b border-surface-200 md:max-h-none max-h-44 overflow-y-auto p-3 space-y-2">
        {data.views.map((view) => {
          const thumb = view.outputs[0] ?? view.inputs[0];
          const isActive = selected?.id === view.id;
          return (
            <button
              key={view.id}
              type="button"
              onClick={() => setSelectedId(view.id)}
              className={`w-full flex items-center gap-3 p-2 rounded-lg border text-left transition-colors ${
                isActive
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-surface-200 hover:bg-surface-50'
              }`}
            >
              <div className="w-14 h-10 shrink-0 rounded bg-surface-100 overflow-hidden flex items-center justify-center">
                {thumb ? (
                  <img src={thumb.url} alt={view.label} loading="lazy" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[9px] text-surface-400">no img</span>
                )}
              </div>
              <span className="text-sm font-medium text-surface-700 truncate">{view.label}</span>
            </button>
          );
        })}
      </div>

      {/* Selected view detail */}
      <div className="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6 space-y-6">
        {selected && (
          <>
            {selected.outputs.length > 0 && (
              <section>
                <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
                  Generated output
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selected.outputs.map((img, i) => (
                    <Thumb
                      key={`out-${img.label}`}
                      image={img}
                      onOpen={() => setCompare({ outIdx: i, inIdx: 0 })}
                    />
                  ))}
                </div>
              </section>
            )}

            <section>
              <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
                Generation inputs
              </h4>
              {selected.inputs.length === 0 ? (
                <p className="text-sm text-surface-400 italic">No input images available.</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selected.inputs.map((img, i) => (
                    <Thumb
                      key={`in-${img.label}`}
                      image={img}
                      onOpen={() => setCompare({ outIdx: 0, inIdx: i })}
                    />
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider">
                  Prompt
                  {selected.prompt_view && (
                    <span className="ml-2 font-mono text-[10px] normal-case text-surface-400">
                      {selected.prompt_view}
                    </span>
                  )}
                </h4>
                {selected.prompt && (
                  <div className="flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => setPromptExpanded((v) => !v)}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-surface-600 bg-surface-100 hover:bg-surface-200 rounded-md transition-colors"
                      aria-expanded={promptExpanded}
                      title={promptExpanded ? 'Collapse prompt' : 'Expand prompt to full height'}
                    >
                      {promptExpanded ? 'Collapse' : 'Expand'}
                    </button>
                    <button
                      type="button"
                      onClick={copyPrompt}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                  </div>
                )}
              </div>
              {selected.prompt ? (
                <pre className="text-xs leading-relaxed text-surface-700 bg-surface-50 border border-surface-200 rounded-lg p-3 whitespace-pre-wrap break-words font-mono max-h-[40vh] overflow-y-auto">
                  {selected.prompt}
                </pre>
              ) : (
                <p className="text-sm text-surface-400 italic">No prompt recorded.</p>
              )}
            </section>
          </>
        )}
      </div>

      {/* Expanded prompt overlay: fills the detail area (grows upward over
          the images) with its own scroll, so a long prompt is fully visible
          even though the modal body itself doesn't scroll. */}
      {selected?.prompt && promptExpanded && (
        <div className="absolute inset-0 md:left-56 z-20 bg-white flex flex-col">
          <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3 border-b border-surface-200 shrink-0 bg-surface-50">
            <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider">
              Prompt
              {selected.prompt_view && (
                <span className="ml-2 font-mono text-[10px] normal-case text-surface-400">
                  {selected.prompt_view}
                </span>
              )}
            </h4>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPromptExpanded(false)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-surface-600 bg-surface-100 hover:bg-surface-200 rounded-md transition-colors"
              >
                Collapse
              </button>
              <button
                type="button"
                onClick={copyPrompt}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-md transition-colors"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
          <pre className="flex-1 min-h-0 overflow-y-auto m-0 text-xs leading-relaxed text-surface-700 bg-surface-50 whitespace-pre-wrap break-words font-mono p-4 sm:p-6">
            {selected.prompt}
          </pre>
        </div>
      )}

      {/* Side-by-side comparison: generated output (left) vs generation
          input (right), each independently navigable with arrows. */}
      {compare && selected && (
        <div className="fixed inset-0 z-[57] flex flex-col bg-black/80">
          <div className="flex items-center justify-between gap-2 px-4 py-3 shrink-0">
            <span className="text-sm font-medium text-white/90 truncate">
              {selected.label} &mdash; side by side
            </span>
            <button
              type="button"
              onClick={() => setCompare(null)}
              aria-label="Close comparison"
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex-1 min-h-0 flex flex-col md:flex-row gap-3 px-3 pb-3">
            <ComparePane
              title="Generated output"
              images={selected.outputs}
              index={compare.outIdx}
              onIndex={(next) => setCompare((c) => (c ? { ...c, outIdx: next } : c))}
              onZoom={openPreview}
            />
            <ComparePane
              title="Generation input"
              images={selected.inputs}
              index={compare.inIdx}
              onIndex={(next) => setCompare((c) => (c ? { ...c, inIdx: next } : c))}
              onZoom={openPreview}
            />
          </div>
        </div>
      )}

      {preview && preview.images[preview.index] && (
        <Lightbox
          images={preview.images}
          index={preview.index}
          onClose={() => setPreview(null)}
          onIndexChange={(next) => setPreview((p) => (p ? { ...p, index: next } : p))}
          onDownload={(image) => downloadImage(image, scanId)}
        />
      )}
      </div>
    </div>
  );
}

interface IntermediatesTabProps {
  data: ScanIntermediatesResponse;
  scanId: string | null;
}

function IntermediatesTab({ data, scanId }: IntermediatesTabProps) {
  const [preview, setPreview] = useState<{ images: ScanImage[]; index: number } | null>(null);

  const openPreview = (images: ScanDebugImage[], image: ScanDebugImage) => {
    const idx = images.findIndex((it) => it.url === image.url);
    if (idx >= 0) setPreview({ images, index: idx });
  };

  if (data.groups.length === 0) {
    return (
      <div className="p-12 text-center text-surface-400 italic">
        No intermediate artefacts available for this scan.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-8">
      {data.groups.map((group) => (
        <section key={group.id}>
          <div className="flex items-center gap-2 mb-3">
            <h4 className="text-sm font-semibold text-surface-800">{group.label}</h4>
            <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-surface-100 text-surface-500">
              {group.images.length} {group.images.length === 1 ? 'image' : 'images'}
            </span>
          </div>
          {group.prompt && (
            <pre className="mb-3 text-xs leading-relaxed text-surface-700 bg-surface-50 border border-surface-200 rounded-lg p-3 whitespace-pre-wrap break-words font-mono max-h-40 overflow-y-auto">
              {group.prompt}
            </pre>
          )}
          {group.images.length === 0 ? (
            <p className="text-sm text-surface-400 italic">No images in this group.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {group.images.map((img) => (
                <Thumb
                  key={`${group.id}-${img.label}`}
                  image={img}
                  onOpen={() => openPreview(group.images, img)}
                />
              ))}
            </div>
          )}
        </section>
      ))}

      {preview && preview.images[preview.index] && (
        <Lightbox
          images={preview.images}
          index={preview.index}
          onClose={() => setPreview(null)}
          onIndexChange={(next) => setPreview((p) => (p ? { ...p, index: next } : p))}
          onDownload={(image) => downloadImage(image, scanId)}
        />
      )}
    </div>
  );
}

function MetadataValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === '') {
    return <span className="text-surface-400 italic">—</span>;
  }
  if (typeof value === 'object') {
    return (
      <pre className="text-xs font-mono text-surface-700 bg-surface-50 border border-surface-200 rounded p-2 whitespace-pre-wrap break-words overflow-x-auto">
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }
  return <span className="text-sm text-surface-800 break-words">{String(value)}</span>;
}

function KeyValueTable({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data);
  if (entries.length === 0) {
    return <p className="text-sm text-surface-400 italic">No fields.</p>;
  }
  return (
    <dl className="divide-y divide-surface-100 border border-surface-200 rounded-lg overflow-hidden">
      {entries.map(([key, value]) => (
        <div key={key} className="grid grid-cols-1 sm:grid-cols-3 gap-1 sm:gap-3 px-3 py-2">
          <dt className="text-xs font-semibold text-surface-500 uppercase tracking-wider break-words">
            {key.replace(/_/g, ' ')}
          </dt>
          <dd className="sm:col-span-2 min-w-0">
            <MetadataValue value={value} />
          </dd>
        </div>
      ))}
    </dl>
  );
}

interface MetadataTabProps {
  data: ScanMetadataResponse;
}

function MetadataTab({ data }: MetadataTabProps) {
  const hasRequest = !!data.request_params && Object.keys(data.request_params).length > 0;
  const hasVehicle = !!data.vehicle_metadata && Object.keys(data.vehicle_metadata).length > 0;

  if (!hasRequest && !hasVehicle) {
    return (
      <div className="p-12 text-center text-surface-400 italic">
        No metadata available for this scan.
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-8">
      <section>
        <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
          Request parameters
        </h4>
        {hasRequest ? (
          <KeyValueTable data={data.request_params as Record<string, unknown>} />
        ) : (
          <p className="text-sm text-surface-400 italic">No request parameters recorded.</p>
        )}
      </section>

      <section>
        <h4 className="text-xs font-semibold text-surface-700 uppercase tracking-wider mb-3">
          Vehicle metadata
          <span className="ml-2 font-mono text-[10px] normal-case text-surface-400">
            vehicle_description.json
          </span>
        </h4>
        {hasVehicle ? (
          <KeyValueTable data={data.vehicle_metadata as Record<string, unknown>} />
        ) : (
          <p className="text-sm text-surface-400 italic">No vehicle metadata recorded.</p>
        )}
      </section>
    </div>
  );
}

interface LogTabProps {
  data: GenerationLogResponse;
}

function LogTab({ data }: LogTabProps) {
  if (data.columns.length === 0 || data.rows.length === 0) {
    return (
      <div className="p-12 text-center text-surface-400 italic">
        The generation log is empty.
      </div>
    );
  }
  return (
    <div className="p-4 sm:p-6">
      <table className="data-table w-full text-xs">
        <thead>
          <tr>
            {data.columns.map((col) => (
              <th
                key={col}
                className="px-3 py-2 text-left font-semibold text-surface-600 uppercase tracking-wider whitespace-nowrap border-b border-surface-200"
              >
                {col.replace(/_/g, ' ')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((row, idx) => (
            <tr key={idx} className="border-b border-surface-100 hover:bg-surface-50">
              {data.columns.map((col) => (
                <td
                  key={col}
                  data-label={col.replace(/_/g, ' ')}
                  className="px-3 py-1.5 text-surface-700 align-top font-mono whitespace-nowrap"
                >
                  {row[col] ?? ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ScanDebugModal({ isOpen, jobId, scanId, onClose }: ScanDebugModalProps) {
  const [tab, setTab] = useState<TabId>('views');
  const [debugData, setDebugData] = useState<ScanDebugResponse | null>(null);
  const [logData, setLogData] = useState<GenerationLogResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);
  const [intermediatesData, setIntermediatesData] = useState<ScanIntermediatesResponse | null>(null);
  const [intermediatesLoading, setIntermediatesLoading] = useState(false);
  const [intermediatesError, setIntermediatesError] = useState<string | null>(null);
  const [metadataData, setMetadataData] = useState<ScanMetadataResponse | null>(null);
  const [metadataLoading, setMetadataLoading] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const logFetchedRef = useRef(false);
  const intermediatesFetchedRef = useRef(false);
  const metadataFetchedRef = useRef(false);

  // Load per-view debug data when opened.
  useEffect(() => {
    if (!isOpen || !jobId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setDebugData(null);
    fetchScanDebug(jobId)
      .then((resp) => {
        if (!cancelled) setDebugData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch debug data for job ${jobId}:`, err);
          setError(err instanceof Error ? err.message : 'Failed to load debug data');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, reloadKey]);

  // Lazily load the generation log the first time its tab is opened.
  // A ref (not state) gates the one-shot fetch so toggling ``logLoading``
  // doesn't re-run this effect and cancel its own in-flight request.
  useEffect(() => {
    if (!isOpen || !jobId || tab !== 'log') return;
    if (logFetchedRef.current) return;
    logFetchedRef.current = true;
    let cancelled = false;
    setLogLoading(true);
    setLogError(null);
    fetchGenerationLog(jobId)
      .then((resp) => {
        if (!cancelled) setLogData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch generation log for job ${jobId}:`, err);
          setLogError(err instanceof Error ? err.message : 'Failed to load generation log');
        }
      })
      .finally(() => {
        if (!cancelled) setLogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, tab]);

  // Lazily load the intermediate/ tree the first time its tab is opened.
  useEffect(() => {
    if (!isOpen || !jobId || tab !== 'intermediates') return;
    if (intermediatesFetchedRef.current) return;
    intermediatesFetchedRef.current = true;
    let cancelled = false;
    setIntermediatesLoading(true);
    setIntermediatesError(null);
    fetchScanIntermediates(jobId)
      .then((resp) => {
        if (!cancelled) setIntermediatesData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch intermediates for job ${jobId}:`, err);
          setIntermediatesError(err instanceof Error ? err.message : 'Failed to load intermediates');
        }
      })
      .finally(() => {
        if (!cancelled) setIntermediatesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, tab]);

  // Lazily load request params + vehicle metadata the first time its tab opens.
  useEffect(() => {
    if (!isOpen || !jobId || tab !== 'metadata') return;
    if (metadataFetchedRef.current) return;
    metadataFetchedRef.current = true;
    let cancelled = false;
    setMetadataLoading(true);
    setMetadataError(null);
    fetchScanMetadata(jobId)
      .then((resp) => {
        if (!cancelled) setMetadataData(resp);
      })
      .catch((err) => {
        if (!cancelled) {
          log.error(`Failed to fetch metadata for job ${jobId}:`, err);
          setMetadataError(err instanceof Error ? err.message : 'Failed to load metadata');
        }
      })
      .finally(() => {
        if (!cancelled) setMetadataLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, jobId, tab]);

  // Reset transient state on close.
  useEffect(() => {
    if (!isOpen) {
      setTab('views');
      setLogData(null);
      setLogError(null);
      setLogLoading(false);
      logFetchedRef.current = false;
      setIntermediatesData(null);
      setIntermediatesError(null);
      setIntermediatesLoading(false);
      intermediatesFetchedRef.current = false;
      setMetadataData(null);
      setMetadataError(null);
      setMetadataLoading(false);
      metadataFetchedRef.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl max-w-7xl w-[96vw] max-h-[92vh] overflow-hidden flex flex-col mx-3 sm:mx-4">
        {/* Header */}
        <div className="shrink-0 px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-200 flex items-center justify-between gap-2 flex-wrap bg-surface-50">
          <div className="flex items-center gap-3 min-w-0">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-surface-800">Scan Debug</h3>
              <div className="flex items-center gap-2">
                {scanId && <p className="text-xs font-mono text-surface-500 truncate">{scanId}</p>}
                {debugData && (
                  <span className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-surface-200 text-surface-600">
                    {debugData.storage}
                  </span>
                )}
                {debugData?.aftermarket?.has_aftermarket && (
                  <span
                    className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-300"
                    title={debugData.aftermarket.description ?? 'Aftermarket wheels/tires detected'}
                  >
                    Aftermarket wheels/tires
                  </span>
                )}
                {debugData?.cover_validation && !debugData.cover_validation.valid && (
                  <span
                    className="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-300"
                    title={
                      debugData.cover_validation.failed_covers.length > 0
                        ? `Cover validation failed: ${debugData.cover_validation.failed_covers.join(', ')}`
                        : 'Cover (OV) validation failed'
                    }
                  >
                    OV validation failed
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Tabs */}
            <div className="flex items-center bg-surface-100 rounded-lg p-0.5">
              <button
                type="button"
                onClick={() => setTab('views')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  tab === 'views' ? 'bg-white text-surface-800 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                }`}
              >
                Views
              </button>
              <button
                type="button"
                onClick={() => setTab('metadata')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  tab === 'metadata' ? 'bg-white text-surface-800 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                }`}
              >
                Metadata
              </button>
              <button
                type="button"
                onClick={() => setTab('intermediates')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  tab === 'intermediates' ? 'bg-white text-surface-800 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                }`}
              >
                Intermediates
              </button>
              <button
                type="button"
                onClick={() => setTab('log')}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  tab === 'log' ? 'bg-white text-surface-800 shadow-sm' : 'text-surface-500 hover:text-surface-700'
                }`}
              >
                Generation Log
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-surface-500 hover:text-surface-800 hover:bg-surface-100 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          {tab === 'views' && (
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
              {isLoading && (
                <div className="p-12 text-center text-surface-500">Loading debug data...</div>
              )}
              {error && !isLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load debug data</p>
                  <p className="text-sm text-surface-500 mt-1">{error}</p>
                  <button
                    onClick={() => setReloadKey((k) => k + 1)}
                    className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
              {debugData && !isLoading && !error && (
                <ViewsTab data={debugData} scanId={scanId} />
              )}
            </div>
          )}

          {tab === 'metadata' && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              {metadataLoading && (
                <div className="p-12 text-center text-surface-500">Loading metadata...</div>
              )}
              {metadataError && !metadataLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load metadata</p>
                  <p className="text-sm text-surface-500 mt-1">{metadataError}</p>
                </div>
              )}
              {metadataData && !metadataLoading && !metadataError && (
                <MetadataTab data={metadataData} />
              )}
            </div>
          )}

          {tab === 'intermediates' && (
            <div className="flex-1 min-h-0 overflow-y-auto">
              {intermediatesLoading && (
                <div className="p-12 text-center text-surface-500">Loading intermediates...</div>
              )}
              {intermediatesError && !intermediatesLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load intermediates</p>
                  <p className="text-sm text-surface-500 mt-1">{intermediatesError}</p>
                </div>
              )}
              {intermediatesData && !intermediatesLoading && !intermediatesError && (
                <IntermediatesTab data={intermediatesData} scanId={scanId} />
              )}
            </div>
          )}

          {tab === 'log' && (
            <div className="flex-1 min-h-0 overflow-auto">
              {logLoading && (
                <div className="p-12 text-center text-surface-500">Loading generation log...</div>
              )}
              {logError && !logLoading && (
                <div className="p-12 text-center">
                  <p className="text-surface-700 font-medium">Failed to load generation log</p>
                  <p className="text-sm text-surface-500 mt-1">{logError}</p>
                </div>
              )}
              {logData && !logLoading && !logError && <LogTab data={logData} />}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
